import {
  closeCurrentDialog,
  Form,
  FormField,
  FormSubmitHandler,
  FormButton,
  Button,
  ButtonGroup,
  showNotification,
} from 'platform/components';
import {HStack, Box, Grid, Stack, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';

import {
  AddressRequestBodyV2,
  AddressResponseBodyV2,
  CommonAddressResponseBody,
  CompGetInfoV3ApiResponse,
  useConvertToBusinessMutation,
  useCreateCustomerAddressV2Mutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {CountrySelect, DecodeBusinessIdButton, handleApiError, useAddress} from '@dms/shared';

interface BusinessInfoFormProps {
  customerId: string;
  addresses: AddressResponseBodyV2[];
}

export function BusinessInfoForm(props: BusinessInfoFormProps) {
  const {composeAddress} = useAddress();
  const [convertToBusiness] = useConvertToBusinessMutation();
  const [createAddress] = useCreateCustomerAddressV2Mutation();
  const [addressOptions, setAddressOptions] = useState(() =>
    props.addresses.map((address) => ({
      label: composeAddress(address.address),
      value: address.id,
    }))
  );

  const handleSubmit: FormSubmitHandler<BusinessInfoFormSchemaType> = async (values) => {
    const requestBody = {
      customerId: props.customerId,
      convertToBusinessRequestBody: {
        businessInfo: {
          businessInfoData: {
            registrationNumber: values.registrationNumber,
            tradeName: values.tradeName,
            countryOfRegistrationCode: values.countryOfRegistrationCode,
            vatNumber: values.vatNumber ?? null,
            fileNumber: values.fileNumber ?? null,
          },
          businessAddressId: values.businessAddressId ?? null,
        },
      },
    };

    await convertToBusiness(requestBody)
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.success'));
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<BusinessInfoFormSchemaType>
      experimentalZodSchema={BusinessInfoFormSchema}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => {
        const registrationNumber = formApi.watch('registrationNumber');
        const vatNumber = formApi.watch('vatNumber');

        // eslint-disable-next-line complexity -- Pre-existing violation
        const handleDecodeBusinessIdForm = async (company: CompGetInfoV3ApiResponse) => {
          const businessInfoData = {
            countryOfRegistrationCode: company?.[0]?.country ?? '',
            registrationNumber: company?.[0]?.businessId ?? '',
            vatNumber: company?.[0]?.taxId ?? '',
            fileNumber: company?.[0]?.fileNumber ?? '',
            tradeName: company?.[0]?.businessName ?? '',
          };

          const addressData: AddressRequestBodyV2 = {
            address: {
              street: company?.[0]?.street ?? '',
              descriptiveNumber: company?.[0]?.descriptiveNumber ?? null,
              orientationNumber: company?.[0]?.orientationNumber ?? null,
              city: company?.[0]?.city ?? '',
              zip: company?.[0]?.zipCode ?? '',
              state: company?.[0]?.state,
              country: company?.[0]?.country ?? '',
              addressComplement: null,
              prefix: company?.[0]?.prefix ?? null,
              district: null,
            },
            type: null,
            invalid: null,
          };

          const alreadyCreatedAddress = props.addresses?.find(
            (_address) =>
              _address.address.street === addressData.address.street &&
              _address.address.orientationNumber === addressData.address.orientationNumber &&
              _address.address.descriptiveNumber === addressData.address.descriptiveNumber
          );

          if (alreadyCreatedAddress) {
            formApi.setValue('businessAddressId', alreadyCreatedAddress.id);
          } else {
            await createAddress({
              customerId: props.customerId,
              addressRequestBody: addressData,
            })
              .unwrap()
              .then((address) => {
                setAddressOptions((prev) => [
                  ...prev,
                  {
                    label: composeAddress(addressData.address as CommonAddressResponseBody),
                    value: address.id,
                  },
                ]);

                formApi.setValue('businessAddressId', address.id);
              })
              .catch(handleApiError);
          }

          formApi.setValue('vatNumber', businessInfoData.vatNumber);
          formApi.setValue('registrationNumber', businessInfoData.registrationNumber);
          formApi.setValue('countryOfRegistrationCode', businessInfoData.countryOfRegistrationCode);
          formApi.setValue('fileNumber', businessInfoData.fileNumber);
          formApi.setValue('tradeName', businessInfoData.tradeName);
        };

        return (
          <VStack spacing={4}>
            <Stack direction={['column', 'row']} spacing={4}>
              <Box flex={1}>
                <FormField
                  isRequired
                  control={control}
                  type="text"
                  name="registrationNumber"
                  label={i18n.t('entity.businessInfo.labels.registrationNumber')}
                />
              </Box>
              <Box flex={1}>
                <FormField
                  control={control}
                  type="text"
                  name="vatNumber"
                  label={i18n.t('entity.businessInfo.labels.vatNumber')}
                />
              </Box>
              <HStack height={[undefined, 13]} align="flex-end">
                <DecodeBusinessIdButton
                  businessId={registrationNumber}
                  vatId={vatNumber}
                  onSuccess={(companyData) => handleDecodeBusinessIdForm(companyData)}
                />
              </HStack>
            </Stack>
            <Grid columns={1}>
              <FormField
                isRequired
                control={control}
                type="text"
                label={i18n.t('entity.businessInfo.labels.tradeName')}
                name="tradeName"
              />
            </Grid>
            <Grid columns={[1, 2]}>
              <FormField
                control={control}
                type="text"
                label={i18n.t('entity.businessInfo.labels.fileNumber')}
                name="fileNumber"
              />
              <CountrySelect<BusinessInfoFormSchemaType>
                control={control}
                name="countryOfRegistrationCode"
                label={i18n.t('entity.businessInfo.labels.countryOfRegistrationCode')}
                isRequired
                menuInPortal
              />
            </Grid>
            <Grid columns={1}>
              <FormField
                isRequired
                control={control}
                options={addressOptions}
                type="choice"
                label={i18n.t('entity.businessInfo.labels.businessAddressId')}
                name="businessAddressId"
              />
            </Grid>
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.cancel')}
                onClick={closeCurrentDialog}
              />
              <FormButton control={control} type="submit" title={i18n.t('general.actions.save')} />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}

const BusinessInfoFormSchema = z.object({
  businessAddressId: z.string().min(1),
  countryOfRegistrationCode: z.string(),
  registrationNumber: z.string().min(1),
  vatNumber: z.string().optional(),
  tradeName: z.string().min(1),
  fileNumber: z.string().optional().nullable(),
});

type BusinessInfoFormSchemaType = z.infer<typeof BusinessInfoFormSchema>;
